import { ENV, Environment } from '@repo/constants'
import { FEATURE_FLAGS_ENV } from '@repo/feature-flags'

type Config = {
  env: Environment
  backendURL: string
  featureFlags: (typeof FEATURE_FLAGS_ENV)[Environment]
  /** Where the team reads client recordings; only where the recorder runs. */
  testLabURL: string | null
  isDevMode: boolean
}

// Unlike the other portals, local dev derives the host from window.location rather
// than assuming localhost: this portal is also opened from the mobile app's in-app
// browser, which reaches the dev server on a LAN IP.
const localHost = (port: number): string =>
  typeof window === 'undefined'
    ? `http://localhost:${port}`
    : `${window.location.protocol}//${window.location.hostname}:${port}`

const base: Pick<Config, 'isDevMode'> = {
  isDevMode: import.meta.env.VITE_IS_DEV_MODE === 'true',
}

const local: Config = {
  ...base,
  env: ENV.LOCAL,
  featureFlags: FEATURE_FLAGS_ENV.LOCAL,
  testLabURL: 'http://localhost:5190',
  backendURL: localHost(8080),
}

const dev: Config = {
  ...base,
  env: ENV.DEV,
  featureFlags: FEATURE_FLAGS_ENV.DEV,
  testLabURL: 'https://dev.test-lab.petrafi.com',
  backendURL: 'https://dev.api.petrafi.com',
}

const staging: Config = {
  ...base,
  env: ENV.STAGING,
  featureFlags: FEATURE_FLAGS_ENV.STAGING,
  testLabURL: 'https://dev.test-lab.petrafi.com',
  backendURL: 'https://staging.api.petrafi.com',
}

const prod: Config = {
  ...base,
  env: ENV.PROD,
  featureFlags: FEATURE_FLAGS_ENV.PROD,
  testLabURL: 'https://dev.test-lab.petrafi.com',
  backendURL: 'https://api.petrafi.com',
}

const configs: Record<Environment, Config> = {
  [ENV.LOCAL]: local,
  [ENV.DEV]: dev,
  [ENV.PROD]: prod,
  [ENV.STAGING]: staging,
}

function getEnvConfig(): Config {
  const env = import.meta.env.VITE_FRONTEND_ENV as Environment | undefined
  if (env && env in configs) {
    return configs[env]
  }
  return local
}

const config = getEnvConfig()
export default config
